import CreateSpacePage from "./pages/CreateSpacePage";
import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { Toaster } from "@/components/ui/sonner";
import RanneaIntro from "@/components/RanneaIntro";
import { InstallPrompt } from "@/components/InstallPrompt";
import LoginPage from "@/pages/LoginPage";
import PaymentThanksPage from "@/pages/PaymentThanksPage";
import InvitationPage from "@/pages/InvitationPage";
import SuperadminPage from "@/pages/SuperadminPage";
import Layout from "@/components/Layout";
import DashboardPage from "@/pages/DashboardPage";
import LibraryPage from "@/pages/LibraryPage";
import DocumentsPage from "@/pages/DocumentsPage";
import AgendaPage from "@/pages/AgendaPage";
import EventDetailPage from "@/pages/EventDetailPage";
import AchatsPage from "@/pages/AchatsPage";
import PresencesPage from "@/pages/PresencesPage";
import SetlistsPage from "@/pages/SetlistsPage";
import InventairePage from "@/pages/InventairePage";
import FavorisPage from "@/pages/FavorisPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import UtilisateursPage from "@/pages/UtilisateursPage";
import ParametresPage from "@/pages/ParametresPage";
import TeachingDashboard from "@/pages/enseignement/TeachingDashboard";
import ElevesPage from "@/pages/enseignement/ElevesPage";
import EleveFichePage from "@/pages/enseignement/EleveFichePage";
import ClassesPage from "@/pages/enseignement/ClassesPage";
import ClassePage from "@/pages/enseignement/ClassePage";
import AgendaTeachingPage from "@/pages/enseignement/AgendaTeachingPage";
import MesCoursPage from "@/pages/enseignement/MesCoursPage";
import RessourcesPage from "@/pages/enseignement/RessourcesPage";
import MesRessourcesPage from "@/pages/enseignement/MesRessourcesPage";
import AssignmentsPage from "@/pages/enseignement/AssignmentsPage";

function HomeSwitch() {
  const { tspace } = useAuth();
  return tspace ? <TeachingDashboard /> : <DashboardPage />;
}

function Protected({ children }) {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === "loading") {
    return (
        <div className="min-h-screen app-bg flex items-center justify-center" data-testid="app-loading">
        <img src="/rannea-logo-white.png" alt="RANNEA" className="w-56 anim-logo-splash" data-testid="loading-logo" />
      </div>
    );
  }
  if (status === "anon") return <Navigate to={`/connexion?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (user?.is_superadmin) return <Navigate to="/superadmin" replace />;
  return children;
}

function SuperOnly({ children }) {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === "loading") {
    return (
        <div className="min-h-screen app-bg flex items-center justify-center" data-testid="app-loading">
        <img src="/rannea-logo-white.png" alt="RANNEA" className="w-56 anim-logo-splash" />
      </div>
    );
  }
  if (status === "anon") return <Navigate to={`/connexion?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (!user?.is_superadmin) return <Navigate to="/" replace />;
  return children;
}

function App() {
  const [introDone, setIntroDone] = useState(() => sessionStorage.getItem("rannea_intro_done") === "1");
  const finishIntro = () => {
    sessionStorage.setItem("rannea_intro_done", "1");
    setIntroDone(true);
  };
  return (
    <AuthProvider>
      <PlayerProvider>
      {!introDone && <RanneaIntro onDone={finishIntro} />}
      {introDone && <InstallPrompt />}
      <BrowserRouter>
        <Routes>
          <Route path="/connexion" element={<LoginPage />} />
          <Route path="/paiement/merci" element={<PaymentThanksPage />} />
          <Route path="/invitation/:token" element={<InvitationPage />} />
          <Route
            path="/superadmin"
            element={
              <SuperOnly>
                <SuperadminPage />
              </SuperOnly>
            }
          />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Navigate to="/tableau-de-bord" replace />} />
            <Route path="tableau-de-bord" element={<HomeSwitch />} />
            <Route path="enseignement/eleves" element={<ElevesPage />} />
            <Route path="enseignement/eleves/:relId" element={<EleveFichePage />} />
            <Route path="enseignement/classes" element={<ClassesPage />} />
            <Route path="enseignement/classes/:classId" element={<ClassePage />} />
            <Route path="enseignement/agenda" element={<AgendaTeachingPage />} />
            <Route path="enseignement/cours" element={<MesCoursPage />} />
            <Route path="enseignement/ressources" element={<RessourcesPage />} />
            <Route path="enseignement/mes-ressources" element={<MesRessourcesPage />} />
            <Route path="enseignement/travail" element={<AssignmentsPage />} />
            <Route path="bibliotheque" element={<LibraryPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="agenda" element={<AgendaPage />} />
            <Route path="agenda/:eventId" element={<EventDetailPage />} />
            <Route path="achats" element={<AchatsPage />} />
            <Route path="presence" element={<PresencesPage />} />
            <Route path="inventaire" element={<InventairePage />} />
            <Route path="favoris" element={<FavorisPage />} />
            <Route path="setlists" element={<SetlistsPage />} />
            <Route path="aide" element={<HelpCenterPage />} />
            <Route path="utilisateurs" element={<UtilisateursPage />} />
            <Route path="parametres" element={<ParametresPage />} />
            <Route path="espaces/nouveau" element={<CreateSpacePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-center" />
      </PlayerProvider>
    </AuthProvider>
  );
}

export default App;
